/**
 * Quality Reports Page
 * BuilderBel Construction Project Management System
 *
 * Lists all quality reports for the current project with filtering, stats,
 * workflow actions (complete, review, approve, reject), and PDF download.
 */

import {
  Award,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Download,
  Eye,
  Pencil,
  Plus,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { DatePicker, SearchableDropdown, TextArea } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import QualityReportDrawer from '../components/qualityReports/QualityReportDrawer';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useApproveQualityReportMutation,
  useCompleteQualityReportMutation,
  useDeleteQualityReportMutation,
  useGetQualityReportStatsQuery,
  useGetQualityReportsQuery,
  useRejectQualityReportMutation,
  useReviewQualityReportMutation,
} from '../services/qualityReportApi';
import type {
  QualityReport,
  QualityReportListParams,
  QualityReportStatus,
  QualityOverallResult,
} from '../types/qualityReport';

// ============================================================================
// Constants
// ============================================================================

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'closed', label: 'Closed' },
];

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'ncr', label: 'NCR' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'audit', label: 'Audit' },
];

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'structural', label: 'Structural' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'finishes', label: 'Finishes' },
  { value: 'sitework', label: 'Sitework' },
  { value: 'fire_safety', label: 'Fire Safety' },
  { value: 'general', label: 'General' },
];

const PRIORITY_FILTER_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'scheduledDate', label: 'Scheduled Date' },
  { value: 'title', label: 'Title' },
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'reportNumber', label: 'Report Number' },
];

const PAGE_SIZE = 20;

// ============================================================================
// Helpers
// ============================================================================

const getStatusVariant = (
  status: QualityReportStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'closed':
      return 'neutral';
    case 'completed':
      return 'info';
    case 'reviewed':
      return 'purple';
    case 'in_progress':
      return 'warning';
    case 'rejected':
      return 'error';
    case 'draft':
    default:
      return 'neutral';
  }
};

const getResultVariant = (
  result: QualityOverallResult
): 'success' | 'warning' | 'error' | 'neutral' => {
  switch (result) {
    case 'satisfactory':
      return 'success';
    case 'needs_improvement':
      return 'warning';
    case 'unsatisfactory':
      return 'error';
    case 'pending':
    default:
      return 'neutral';
  }
};

const getPriorityVariant = (
  priority: string
): 'error' | 'warning' | 'info' | 'neutral' => {
  switch (priority) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    case 'low':
    default:
      return 'neutral';
  }
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ============================================================================
// Reject Dialog
// ============================================================================

interface RejectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}

const RejectDialog: React.FC<RejectDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-[110] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-2">Reject Report</h3>
        <p className="text-sm text-gray-500 mb-4">
          Please provide a reason for rejecting this quality report.
        </p>
        <TextArea
          rows={3}
          placeholder="Rejection reason..."
          value={reason}
          onChange={setReason}
        />
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => reason.trim() && onConfirm(reason.trim())}
            disabled={isLoading || !reason.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
          >
            {isLoading ? 'Rejecting...' : 'Reject Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const QualityReports: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<QualityReport | null>(null);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [reportToReject, setReportToReject] = useState<QualityReport | null>(null);

  // Build query params
  const queryParams = useMemo<QualityReportListParams>(() => {
    const params: QualityReportListParams = {
      page: currentPage,
      limit: PAGE_SIZE,
      sortBy: sortBy as QualityReportListParams['sortBy'],
      sortOrder,
    };
    if (statusFilter) params.status = statusFilter as QualityReportListParams['status'];
    if (typeFilter) params.reportType = typeFilter as QualityReportListParams['reportType'];
    if (categoryFilter) params.category = categoryFilter as QualityReportListParams['category'];
    if (priorityFilter) params.priority = priorityFilter as QualityReportListParams['priority'];
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return params;
  }, [currentPage, sortBy, sortOrder, statusFilter, typeFilter, categoryFilter, priorityFilter, dateFrom, dateTo]);

  // RTK Query
  const { data: reportsData, isLoading: isLoadingReports } = useGetQualityReportsQuery(
    { projectId: projectId || '', params: queryParams },
    { skip: !projectId }
  );

  const { data: statsData } = useGetQualityReportStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deleteReport, { isLoading: isDeleting }] = useDeleteQualityReportMutation();
  const [completeReport, { isLoading: isCompleting }] = useCompleteQualityReportMutation();
  const [reviewReport, { isLoading: isReviewing }] = useReviewQualityReportMutation();
  const [approveReport, { isLoading: isApproving }] = useApproveQualityReportMutation();
  const [rejectReport, { isLoading: isRejecting }] = useRejectQualityReportMutation();

  const reports = reportsData?.data?.reports ?? [];
  const pagination = reportsData?.data?.pagination;
  const stats = statsData?.data;

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleOpenCreate = useCallback(() => {
    setSelectedReport(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  }, []);

  const handleOpenEdit = useCallback((report: QualityReport) => {
    setSelectedReport(report);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedReport(null);
  }, []);

  const handleDelete = useCallback(
    async (report: QualityReport) => {
      if (!confirm(`Delete report "${report.title}"? This cannot be undone.`)) return;
      try {
        await deleteReport({ projectId: projectId || '', reportId: report._id }).unwrap();
        toast.success('Quality report deleted');
      } catch {
        toast.error('Failed to delete quality report');
      }
    },
    [deleteReport, projectId, toast]
  );

  const handleComplete = useCallback(
    async (report: QualityReport) => {
      try {
        await completeReport({ projectId: projectId || '', reportId: report._id }).unwrap();
        toast.success('Report marked as completed');
      } catch {
        toast.error('Failed to complete report');
      }
    },
    [completeReport, projectId, toast]
  );

  const handleReview = useCallback(
    async (report: QualityReport) => {
      try {
        await reviewReport({
          projectId: projectId || '',
          reportId: report._id,
          data: {},
        }).unwrap();
        toast.success('Report marked as reviewed');
      } catch {
        toast.error('Failed to review report');
      }
    },
    [reviewReport, projectId, toast]
  );

  const handleApprove = useCallback(
    async (report: QualityReport) => {
      try {
        await approveReport({ projectId: projectId || '', reportId: report._id }).unwrap();
        toast.success('Report approved');
      } catch {
        toast.error('Failed to approve report');
      }
    },
    [approveReport, projectId, toast]
  );

  const handleOpenReject = useCallback((report: QualityReport) => {
    setReportToReject(report);
    setRejectDialogOpen(true);
  }, []);

  const handleConfirmReject = useCallback(
    async (reason: string) => {
      if (!reportToReject) return;
      try {
        await rejectReport({
          projectId: projectId || '',
          reportId: reportToReject._id,
          data: { rejectionReason: reason },
        }).unwrap();
        toast.success('Report rejected');
        setRejectDialogOpen(false);
        setReportToReject(null);
      } catch {
        toast.error('Failed to reject report');
      }
    },
    [rejectReport, projectId, reportToReject, toast]
  );

  const handleDownloadPDF = useCallback(
    async (report: QualityReport) => {
      try {
        const token = localStorage.getItem('auth_token');
        const url = `/api/projects/${projectId}/quality-reports/${report._id}/pdf`;
        const response = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          toast.error('Failed to generate PDF');
          return;
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `quality-report-${report.reportNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  // ============================================================================
  // Action menu builder
  // ============================================================================

  const getActions = useCallback(
    (report: QualityReport): ActionMenuItem[] => {
      const isImmutable = report.status === 'approved' || report.status === 'closed';
      const actions: ActionMenuItem[] = [
        {
          label: 'View Details',
          icon: <Eye className="h-4 w-4" />,
          onClick: () => handleOpenEdit(report),
        },
      ];

      if (!isImmutable) {
        actions.push({
          label: 'Edit',
          icon: <Pencil className="h-4 w-4" />,
          onClick: () => handleOpenEdit(report),
        });
      }

      if (report.status === 'draft' || report.status === 'in_progress') {
        actions.push({
          label: 'Mark Complete',
          icon: <CheckCircle className="h-4 w-4" />,
          onClick: () => handleComplete(report),
        });
      }

      if (report.status === 'completed') {
        actions.push({
          label: 'Mark Reviewed',
          icon: <ClipboardCheck className="h-4 w-4" />,
          onClick: () => handleReview(report),
        });
      }

      if (report.status === 'reviewed' || report.status === 'completed') {
        actions.push({
          label: 'Approve',
          icon: <CheckCircle className="h-4 w-4" />,
          onClick: () => handleApprove(report),
        });
        actions.push({
          label: 'Reject',
          icon: <XCircle className="h-4 w-4" />,
          onClick: () => handleOpenReject(report),
          variant: 'danger',
        });
      }

      actions.push({
        label: 'Download PDF',
        icon: <Download className="h-4 w-4" />,
        onClick: () => handleDownloadPDF(report),
      });

      if (!isImmutable) {
        actions.push({
          label: 'Delete',
          icon: <Trash2 className="h-4 w-4" />,
          onClick: () => handleDelete(report),
          variant: 'danger',
        });
      }

      return actions;
    },
    [handleOpenEdit, handleComplete, handleReview, handleApprove, handleOpenReject, handleDownloadPDF, handleDelete]
  );

  // ============================================================================
  // Stats cards
  // ============================================================================

  const moduleStats = useMemo(() => {
    if (!stats) return [];
    return [
      { title: 'Total Reports', value: stats.total },
      { title: 'Pass Rate', value: `${stats.passRate}%` },
      { title: 'Pending Review', value: stats.pendingReview },
      { title: 'Open Items', value: stats.openItems },
    ];
  }, [stats]);

  // ============================================================================
  // Render
  // ============================================================================

  const isActionLoading = isDeleting || isCompleting || isReviewing || isApproving || isRejecting;

  return (
    <ModuleLayout
      title="Quality Reports"
      description="Track quality control inspections, NCRs, checklists, and audits"
      icon={<Award className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'Create Report',
        onClick: handleOpenCreate,
        icon: <Plus className="h-4 w-4 mr-2" />,
      }}
      stats={moduleStats}
    >
      {/* ======================================================================
          FILTERS
      ====================================================================== */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-40">
          <SearchableDropdown
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={val => { setStatusFilter(val); setCurrentPage(1); }}
            placeholder="All Status"
          />
        </div>
        <div className="w-40">
          <SearchableDropdown
            options={TYPE_FILTER_OPTIONS}
            value={typeFilter}
            onChange={val => { setTypeFilter(val); setCurrentPage(1); }}
            placeholder="All Types"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={CATEGORY_FILTER_OPTIONS}
            value={categoryFilter}
            onChange={val => { setCategoryFilter(val); setCurrentPage(1); }}
            placeholder="All Categories"
          />
        </div>
        <div className="w-36">
          <SearchableDropdown
            options={PRIORITY_FILTER_OPTIONS}
            value={priorityFilter}
            onChange={val => { setPriorityFilter(val); setCurrentPage(1); }}
            placeholder="All Priorities"
          />
        </div>
        <div className="w-36">
          <DatePicker
            value={dateFrom}
            onChange={val => { setDateFrom(val); setCurrentPage(1); }}
            placeholder="Date from"
          />
        </div>
        <div className="w-36">
          <DatePicker
            value={dateTo}
            onChange={val => { setDateTo(val); setCurrentPage(1); }}
            placeholder="Date to"
          />
        </div>
        <div className="w-44">
          <SearchableDropdown
            options={SORT_OPTIONS}
            value={sortBy}
            onChange={val => { setSortBy(val); setCurrentPage(1); }}
            placeholder="Sort by"
          />
        </div>
      </div>

      {/* ======================================================================
          TABLE
      ====================================================================== */}
      {isLoadingReports ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : reports.length === 0 ? (
        <EmptyState
          icon={Award}
          title="No quality reports found"
          description="Track quality inspections, non-conformance reports, checklists, and audits for this project."
          action={{
            label: 'Create Quality Report',
            onClick: handleOpenCreate,
          }}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table header */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Report
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Result
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheduled
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reports.map(report => {
                  const passItems = report.items.filter(i => i.status === 'pass').length;
                  const failItems = report.items.filter(i => i.status === 'fail').length;

                  return (
                    <tr
                      key={report._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {/* Report number + title */}
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                            {report.title}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">{report.reportNumber}</p>
                          {report.location && (
                            <p className="text-xs text-gray-400">{report.location}</p>
                          )}
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700 capitalize">
                          {report.reportType.toUpperCase()}
                        </span>
                      </td>

                      {/* Category */}
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 capitalize">
                          {report.category.replace(/_/g, ' ')}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={report.status.replace(/_/g, ' ')}
                          variant={getStatusVariant(report.status)}
                        />
                      </td>

                      {/* Priority */}
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={report.priority}
                          variant={getPriorityVariant(report.priority)}
                        />
                      </td>

                      {/* Overall result */}
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={report.overallResult.replace(/_/g, ' ')}
                          variant={getResultVariant(report.overallResult)}
                        />
                      </td>

                      {/* Items summary */}
                      <td className="px-4 py-3">
                        {report.items.length > 0 ? (
                          <div className="text-xs text-gray-600">
                            <span className="text-green-600 font-medium">{passItems}P</span>
                            {' / '}
                            <span className="text-red-600 font-medium">{failItems}F</span>
                            {' / '}
                            <span className="text-gray-400">{report.items.length}T</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>

                      {/* Scheduled date */}
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {formatDate(report.scheduledDate)}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <ActionMenu
                          items={getActions(report)}
                          id={`action-menu-${report._id}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} reports
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={!pagination.hasPrev}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600" />
                </button>
                <span className="text-xs text-gray-600 px-2">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!pagination.hasNext}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
                >
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======================================================================
          DRAWER
      ====================================================================== */}
      <QualityReportDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        projectId={projectId || ''}
        report={selectedReport}
        mode={drawerMode}
      />

      {/* ======================================================================
          REJECT DIALOG
      ====================================================================== */}
      <RejectDialog
        isOpen={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setReportToReject(null);
        }}
        onConfirm={handleConfirmReject}
        isLoading={isRejecting}
      />
    </ModuleLayout>
  );
};

export default QualityReports;
